import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface Stats {
  assets: { total: number; by_type: Record<string, number> };
  history: { total_candles: number; latest_date: string; oldest_date: string };
  legacy: { stocks: number; bonds: number; stock_values: number; bond_values: number };
  sources: { total: number; active: number };
  exchange_rates: { total_rates: number; currencies: number; latest_date: string };
}

const TYPE_CONFIG: Record<string, { label: string; icon: string; color: string; path: string }> = {
  stock: { label: 'Aktien', icon: 'fa-solid fa-building-columns', color: '#00D97E', path: '/stocks' },
  bond: { label: 'Anleihen', icon: 'fa-solid fa-file-contract', color: '#FBBF24', path: '/bonds' },
  index: { label: 'Indizes', icon: 'fa-solid fa-chart-column', color: '#3B82F6', path: '/indices' },
  commodity: { label: 'Rohstoffe', icon: 'fa-solid fa-industry', color: '#F97316', path: '/commodities' },
  crypto: { label: 'Krypto', icon: 'fa-solid fa-coins', color: '#8B5CF6', path: '/crypto' },
  forex: { label: 'Devisen', icon: 'fa-solid fa-money-bill-transfer', color: '#EC4899', path: '/forex' },
};

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/market/dashboard-stats`)
      .then(res => res.json())
      .then(setStats)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={{ color: '#4A5068', padding: '40px', textAlign: 'center' }}>Lade Dashboard...</div>;
  }

  if (!stats) {
    return <div style={{ color: '#FF4757', padding: '40px', textAlign: 'center' }}>Fehler beim Laden</div>;
  }

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '4px' }}>Dashboard</h1>
      <p style={{ color: '#7B8399', fontSize: '0.8rem', marginBottom: '24px' }}>Market Data Service — Übersicht</p>

      {/* Asset type cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {Object.entries(TYPE_CONFIG).map(([type, config]) => (
          <div key={type} style={{
            backgroundColor: '#151921',
            border: '1px solid #1E2330',
            borderRadius: '12px',
            padding: '16px',
            cursor: 'pointer',
            transition: 'all 0.15s',
          }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = config.color + '60')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = '#1E2330')}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontSize: '1.2rem', color: config.color }}>
                <i className={config.icon}></i>
              </span>
              <span style={{
                fontSize: '0.55rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                padding: '2px 6px', borderRadius: '4px',
                backgroundColor: config.color + '20', color: config.color,
              }}>
                {type}
              </span>
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 800 }}>
              {(stats.assets.by_type[type] || 0).toLocaleString('de-DE')}
            </div>
            <div style={{ fontSize: '0.7rem', color: '#4A5068' }}>{config.label}</div>
          </div>
        ))}
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {/* Total Assets */}
        <StatCard
          title="Assets gesamt"
          value={stats.assets.total.toLocaleString('de-DE')}
          subtitle={`${Object.keys(stats.assets.by_type).length} Kategorien`}
          icon="fa-solid fa-cubes"
        />

        {/* Historical Data */}
        <StatCard
          title="Historische Kurse"
          value={stats.history.total_candles.toLocaleString('de-DE')}
          subtitle={`${stats.history.oldest_date} bis ${stats.history.latest_date}`}
          icon="fa-solid fa-chart-pie"
        />

        {/* API Sources */}
        <StatCard
          title="API Quellen"
          value={`${stats.sources.active} / ${stats.sources.total}`}
          subtitle="aktiv / gesamt"
          icon="fa-solid fa-plug"
        />

        {/* Exchange Rates */}
        <StatCard
          title="Wechselkurse"
          value={stats.exchange_rates.total_rates.toLocaleString('de-DE')}
          subtitle={`${stats.exchange_rates.currencies} Währungen · bis ${stats.exchange_rates.latest_date}`}
          icon="fa-solid fa-euro-sign"
        />
      </div>

      {/* Legacy data row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
        <StatCard
          title="TRA Aktien (Legacy)"
          value={stats.legacy.stocks.toString()}
          subtitle={`${stats.legacy.stock_values.toLocaleString('de-DE')} Tageskurse`}
          icon="fa-solid fa-folder"
          muted
        />
        <StatCard
          title="TRA Anleihen (Legacy)"
          value={stats.legacy.bonds.toString()}
          subtitle={`${stats.legacy.bond_values.toLocaleString('de-DE')} Tageskurse`}
          icon="fa-solid fa-folder"
          muted
        />
      </div>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon, muted }: {
  title: string; value: string; subtitle: string; icon: string; muted?: boolean;
}) {
  return (
    <div style={{
      backgroundColor: '#151921',
      border: '1px solid #1E2330',
      borderRadius: '12px',
      padding: '20px',
      opacity: muted ? 0.6 : 1,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#4A5068' }}>
          {title}
        </span>
        <span style={{ fontSize: '1rem', color: '#7B8399' }}>
          <i className={icon}></i>
        </span>
      </div>
      <div style={{ fontSize: '1.8rem', fontWeight: 800, fontFamily: 'monospace', marginBottom: '4px' }}>
        {value}
      </div>
      <div style={{ fontSize: '0.7rem', color: '#7B8399' }}>{subtitle}</div>
    </div>
  );
}
